import type { User } from "@/features/users/schemas/user";

import { getUserById } from "./users";

/**
 * Service result type for current user operations
 */
export interface CurrentUserServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

/**
 * Get the current user from Firestore by UID
 * This service function can be used in contexts where hooks are not available
 *
 * @param uid - The Firebase Auth UID of the current user
 * @returns Promise with the current user data or error
 *
 * @example
 * ```typescript
 * const result = await getCurrentUserFromFirestore(authUser.uid);
 * if (result.success && result.data) {
 *   console.log('Current user:', result.data.name);
 * } else {
 *   console.error('Error:', result.error);
 * }
 * ```
 */
export async function getCurrentUserFromFirestore(
  uid: string
): Promise<CurrentUserServiceResult<User>> {
  try {
    if (!uid) {
      return {
        success: false,
        error: "User UID is required",
        code: "MISSING_UID",
      };
    }

    const user = await getUserById(uid);

    if (!user) {
      return {
        success: false,
        error: "User not found in Firestore",
        code: "USER_NOT_FOUND",
      };
    }

    return {
      success: true,
      data: user,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
      code: "FIRESTORE_ERROR",
    };
  }
}

/**
 * Check if the current user has a specific type
 *
 * @param uid - The Firebase Auth UID of the current user
 * @param expectedType - The expected user type to check
 * @returns Promise with boolean result indicating if user has the expected type
 *
 * @example
 * ```typescript
 * const isAdmin = await isCurrentUserOfType(authUser.uid, "admin");
 * if (isAdmin) {
 *   // User is an admin
 * }
 * ```
 */
export async function isCurrentUserOfType(
  uid: string,
  expectedType: User["type"]
): Promise<CurrentUserServiceResult<boolean>> {
  try {
    const userResult = await getCurrentUserFromFirestore(uid);

    if (!userResult.success || !userResult.data) {
      return {
        success: false,
        error: userResult.error || "User not found",
        code: userResult.code || "USER_NOT_FOUND",
      };
    }

    const hasExpectedType = userResult.data.type === expectedType;

    return {
      success: true,
      data: hasExpectedType,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
      code: "TYPE_CHECK_ERROR",
    };
  }
}
